import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { validateMuPowerDemand, validateMuPowerLeadIn, validateMuPowerUgPath, validateMuPowerUgPoint } from "fond/draw/validation";
import {
  DeleteButton,
  LineStringButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { MuPowerEditableLayerIds, MuPowerInputLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />, <LineStringButton key="drawLineString" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const MuPowerDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const options: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      editOptions: {
        allLayers: [
          {
            ID: MuPowerEditableLayerIds.demand,
            GeometryType: "Point",
            validate: validateMuPowerDemand,
            getFeatureProperties: () => ({
              layerId: MuPowerEditableLayerIds.demand,
              ID: null,
              Type: "SINGLE_PHASE",
              NumFibres: 60,
              SubTypes: null,
              Address: null,
              LeadinType: null,
              Size: null,
            }),
          },
          {
            ID: MuPowerEditableLayerIds.ugPoints,
            GeometryType: "Point",
            validate: validateMuPowerUgPoint,
            getFeatureProperties: () => ({ layerId: MuPowerEditableLayerIds.ugPoints }),
          },
          {
            ID: MuPowerEditableLayerIds.ugPath,
            GeometryType: "LineString",
            validate: validateMuPowerUgPath,
            getFeatureProperties: () => ({ layerId: MuPowerEditableLayerIds.ugPath }),
          },
          {
            ID: MuPowerEditableLayerIds.leadins,
            GeometryType: "LineString",
            validate: validateMuPowerLeadIn,
            getFeatureProperties: () => ({ layerId: MuPowerEditableLayerIds.leadins }),
            options: { midPoints: false },
          },
          {
            ID: MuPowerInputLayerIds.poles,
            GeometryType: "Point",
            Hide: true,
          },
        ],
        editingLayerId: MuPowerEditableLayerIds.demand,
        editingLayerIds: [
          MuPowerEditableLayerIds.demand,
          MuPowerEditableLayerIds.ugPoints,
          MuPowerEditableLayerIds.ugPath,
          MuPowerEditableLayerIds.leadins,
        ],
      },
    }),
    []
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent title="Edit Base Data" alertTitle="Edit base data layers" onFinish={clear} />
    </MultiLayerEditProvider>
  );
};

export default MuPowerDraw;
